/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import techLogo from "assets/img/icons/logoTech.png";
import { useTranslation } from "react-i18next";

import "./ReportTemplateBase.css";

/* O template resolve:
- Tamanho A4
- Header (simples ou profissional)
- Área útil de conteúdo
- Paginação automática pelo usePagedBlocks
- Print igual ao preview (sem página em branco)
- Exibição de página (preview + print) sem quebrar a paginação
- pageNumberStart: permite iniciar a numeração a partir de N (offset)
- totalPages: permite exibir o total GLOBAL (ex.: relatório único com múltiplos templates)
- onPagesCount: callback para o pai saber quantas páginas este template gerou
*/

export type ReportBlock = { key: string; node: React.ReactNode };

function usePagedBlocks(blocks: ReportBlock[]) {
    const [pages, setPages] = useState<number[][]>([]);
    const pageContentRef = useRef<HTMLDivElement | null>(null);
    const blockRefs = useRef<Array<HTMLDivElement | null>>([]);
    const lastSignatureRef = useRef<string>("");
    const rafRef = useRef<number | null>(null);

    // Assinatura estável para detectar mudança real de conteúdo/ordem
    const blocksSignature = useMemo(() => blocks.map((b) => b.key).join("|"), [blocks]);

    // Ao mudar a assinatura, reseta paginação (evita usar pages antigo com blocks novo)
    useLayoutEffect(() => {
        lastSignatureRef.current = "";
        setPages([]);
        blockRefs.current = new Array(blocks.length).fill(null);
    }, [blocksSignature, blocks.length]);

    useLayoutEffect(() => {
        const container = pageContentRef.current;
        if (!container) return;

        // Se não há blocks ainda, não tenta paginar
        if (!blocks.length) return;

        const calcNow = () => {
            const maxHeight = container.getBoundingClientRect().height;
            if (!maxHeight || maxHeight <= 0) return;

            const heights = blocks.map((_, i) => {
                const el = blockRefs.current[i];
                return el ? el.getBoundingClientRect().height : 0;
            });

            // Evita paginar com alturas 0 (ex.: imagem/árvore ainda renderizando)
            if (heights.some((h) => !h || h <= 0)) return;

            const nextPages: number[][] = [];
            let current: number[] = [];
            let currentH = 0;

            const TOL = 2;

            heights.forEach((h, i) => {
                if (current.length && currentH + h > maxHeight - TOL) {
                    nextPages.push(current);
                    current = [i];
                    currentH = h;
                } else {
                    current.push(i);
                    currentH += h;
                }
            });

            if (current.length) nextPages.push(current);

            const signature = nextPages.map((p) => p.join(",")).join("|");
            if (signature !== lastSignatureRef.current) {
                lastSignatureRef.current = signature;
                setPages(nextPages);
            }
        };

        const scheduleCalc = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                calcNow();
            });
        };

        const ro = new ResizeObserver(() => scheduleCalc());
        ro.observe(container);
        blockRefs.current.forEach((el) => el && ro.observe(el));

        // imagens podem alterar altura depois
        const imgs = Array.from(document.querySelectorAll(".report-template img"));
        const onImgLoad = () => scheduleCalc();
        imgs.forEach((img) => img.addEventListener("load", onImgLoad));

        scheduleCalc();

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            ro.disconnect();
            imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
        };
    }, [blocksSignature, blocks]);

    return { pages, pageContentRef, blockRefs };
}

export type ReportHeaderRenderProps = {
    logoSrc: string;
    logoAlt?: string;
    logoWidth?: number;
    title: string;
};

export type ReportHeaderInfo = {
    subtitle?: string;
    company?: { name?: string; cnpj?: string; address?: string };
    metaTopRight?: Array<{ label: string; value: React.ReactNode }>;
    docBox?: Array<{ label: string; value: React.ReactNode }>;
    showRule?: boolean;
};

type ReportTemplateBaseProps = {
    title: string;
    logoSrc: string;
    logoAlt?: string;
    logoWidth?: number;
    blocks: ReportBlock[];
    className?: string;
    renderHeader?: (props: ReportHeaderRenderProps) => React.ReactNode;
    headerInfo?: ReportHeaderInfo;
    showPageNumbers?: boolean;
    pageNumberStart?: number;
    totalPages?: number;
    onPagesCount?: (count: number) => void;
    renderFooter?: (ctx: { page: number; totalPages: number }) => React.ReactNode;
};

function DefaultHeaderSimple({ logoSrc, logoAlt, logoWidth, title }: ReportHeaderRenderProps) {
    return (
        <header className="report-template__top">
            <div className="report-template__header">
                <div className="report-template__header-left">
                    <img src={logoSrc} alt={logoAlt} width={logoWidth} />
                </div>
                <div className="report-template__header-center">
                    <h1 className="report-template__title">{title}</h1>
                </div>
                <div className="report-template__header-right" />
            </div>
        </header>
    );
}

function DefaultHeaderProfessional({
    logoSrc,
    logoAlt,
    logoWidth,
    title,
    headerInfo,
}: ReportHeaderRenderProps & { headerInfo: ReportHeaderInfo }) {
    const { t } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const doc = headerInfo.docBox ?? [];
    const company = headerInfo.company ?? {};

    return (
        <header className="report-template__top report-top">
            {/* GRID: 2 linhas, doc ocupa as 2 */}
            <div className="report-top__grid2rows">
                {/* ROW 1 */}
                <div className="report-top__brand">
                    <img
                        className="report-top__logo"
                        src={logoSrc}
                        alt={logoAlt}
                        style={{ width: logoWidth }}
                    />
                </div>

                <div className="report-top__title">
                    <span>{title}</span>
                    {headerInfo.subtitle ? (
                        <div className="report-top__subtitle">{headerInfo.subtitle}</div>
                    ) : null}
                </div>

                {/* DOC (ocupa row 1 e row 2) */}
                {doc.length ? (
                    <div className="report-top__doc report-top__doc--span2">
                        <div className="report-top__docBox report-top__docBox--mini report-top__docBox--stretch">
                            <div className="report-top__docGrid">
                                {doc.map((d, idx) => (
                                    <div className="report-top__docItem" key={`${d.label}-${idx}`}>
                                        <div className="k">{d.label}:</div>
                                        <div className="v">{d.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div />
                )}

                {/* ROW 2 (empresa) */}
                <div className="report-top__org report-top__org--gridRow2">
                    {company.name ? <div className="report-top__orgTitle">{company.name}</div> : null}

                    {company.cnpj ? (
                        <div className="report-top__orgLine">
                            <span>{tt("pages.thermography.reportTemplateBase.company.cnpj")}:</span> {company.cnpj}
                        </div>
                    ) : null}

                    {company.address ? (
                        <div className="report-top__orgLine report-top__orgLine--address">
                            <span>{tt("pages.thermography.reportTemplateBase.company.address")}:</span> {company.address}
                        </div>
                    ) : null}
                </div>
            </div>

            {headerInfo.showRule !== false ? <div className="report-top__rule" /> : null}
        </header>
    );
}

function DefaultFooter({
    page,
    totalPages,
    showPageNumbers,
    emission,
}: {
    page: number;
    totalPages: number;
    showPageNumbers: boolean;
    emission?: React.ReactNode;
}) {
    const { t } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });

    return (
        <footer className="report-template__footer">
            {/* ESQUERDA */}
            <div className="report-template__footerLeft">
                <img className="report-template__footerLogo" src={techLogo} alt={tt("pages.thermography.reportTemplateBase.footer.techAlt")} />
                <span className="report-template__footerText">
                    {tt("pages.thermography.reportTemplateBase.footer.generatedBy")} {emission}
                </span>
            </div>

            {/* DIREITA */}
            {showPageNumbers ? (
                <div className="report-template__pageNumber">
                    {tt("pages.thermography.reportTemplateBase.footer.pageOf", { page, totalPages })}
                </div>
            ) : (
                <div className="report-template__pageNumber report-template__pageNumber--hidden">.</div>
            )}
        </footer>
    );
}

export default function ReportTemplateBase({
    title,
    logoSrc,
    logoAlt = "logo",
    logoWidth = 100,
    blocks,
    className,
    renderHeader,
    headerInfo,
    showPageNumbers = true,
    renderFooter,
    pageNumberStart = 1,
    totalPages: totalPagesProp,
    onPagesCount,
}: ReportTemplateBaseProps) {
    const { pages, pageContentRef, blockRefs } = usePagedBlocks(blocks);

    const Header = useMemo(() => {
        if (renderHeader) return renderHeader({ logoSrc, logoAlt, logoWidth, title });
        if (headerInfo) {
            return (
                <DefaultHeaderProfessional
                    logoSrc={logoSrc}
                    logoAlt={logoAlt}
                    logoWidth={logoWidth}
                    title={title}
                    headerInfo={headerInfo}
                />
            );
        }
        return <DefaultHeaderSimple logoSrc={logoSrc} logoAlt={logoAlt} logoWidth={logoWidth} title={title} />;
    }, [renderHeader, headerInfo, logoSrc, logoAlt, logoWidth, title]);

    // Proteção: valida se pages ainda é compatível com blocks
    const pagesAreValid = useMemo(() => {
        if (!pages.length) return false;
        if (!blocks.length) return false;
        return pages.every((p) => p.every((i) => i >= 0 && i < blocks.length));
    }, [pages, blocks.length]);

    // Se ainda não calculou OU pages está inválido, mostra 1 página
    const safePages = useMemo(() => {
        return pagesAreValid ? pages : [blocks.map((_, i) => i)];
    }, [pagesAreValid, pages, blocks]);

    // total local
    const localTotalPages = safePages.length;

    // total exibido
    const effectiveTotalPages = totalPagesProp && totalPagesProp > 0 ? totalPagesProp : localTotalPages;

    //avisa ao pai quantas páginas este template gerou
    useEffect(() => {
        onPagesCount?.(localTotalPages);
    }, [localTotalPages, onPagesCount]);

    const Footer = useMemo(() => {
        return (pageLocal1Based: number) => {
            const pageAbs = (pageNumberStart ?? 1) + (pageLocal1Based - 1);

            if (renderFooter) {
                return (
                    <footer className="report-template__footer">
                        {renderFooter({ page: pageAbs, totalPages: effectiveTotalPages })}
                    </footer>
                );
            }

            return (
                <DefaultFooter
                    page={pageAbs}
                    totalPages={effectiveTotalPages}
                    showPageNumbers={showPageNumbers}
                    emission={headerInfo?.docBox?.[0]?.value}
                />
            );
        };
    }, [renderFooter, showPageNumbers, effectiveTotalPages, pageNumberStart, headerInfo?.docBox]);

    const Measure = (
        <div className="report-template__measure">
            <div className="report-template__page">
                <div className="report-template__page-inner">
                    {Header}

                    <div className="report-template__content report-template__content--measurer" ref={pageContentRef}>
                        {blocks.map((b, i) => (
                            <div
                                key={b.key}
                                ref={(el) => (blockRefs.current[i] = el)}
                                className="report-template__block"
                            >
                                {b.node}
                            </div>
                        ))}
                    </div>

                    {Footer(1)}
                </div>
            </div>
        </div>
    );

    return (
        <div className={`report-template ${className ?? ""}`.trim()}>
            {Measure}

            {safePages.map((pageIdxs, idx) => (
                <div className="report-template__page" key={idx}>
                    <div className="report-template__page-inner">
                        {Header}

                        <div className="report-template__content">
                            {pageIdxs.map((i) => {
                                const b = blocks[i];
                                if (!b) return null;

                                return (
                                    <div key={b.key} className="report-template__block">
                                        {b.node}
                                    </div>
                                );
                            })}
                        </div>

                        {Footer(idx + 1)}
                    </div>
                </div>
            ))}
        </div>
    );
}
